import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { PageHeader } from "@/components/app-shell";
import { CalcShell, Field, ListInput, NumberInput, ResultCard, Select, parseNumberList } from "@/components/calculators/CalcShell";
import { getCalcSpec } from "@/lib/calc-specs";
import { useActiveCompany } from "@/hooks/use-active-company";

export const Route = createFileRoute("/_authenticated/calculations/verktoy/$id")({
  component: CalcToolPage,
});

function CalcToolPage() {
  const { id } = Route.useParams();
  const spec = getCalcSpec(id);
  const { activeModuleKey } = useActiveCompany();
  const navigate = useNavigate();

  const [numbers, setNumbers] = useState<Record<string, number | "">>({});
  const [selects, setSelects] = useState<Record<string, string>>({});
  const [lists, setLists] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!spec) return;
    const nums: Record<string, number | ""> = {};
    const sels: Record<string, string> = {};
    const lsts: Record<string, string> = {};
    for (const f of spec.fields) {
      if (f.type === "select") sels[f.key] = f.default;
      else if (f.type === "list") lsts[f.key] = f.default ?? "";
      else nums[f.key] = f.default ?? "";
    }
    setNumbers(nums);
    setSelects(sels);
    setLists(lsts);
  }, [spec]);

  useEffect(() => {
    if (!spec) navigate({ to: "/calculations", replace: true });
  }, [spec, navigate]);

  const outcome = useMemo(() => {
    if (!spec) return { result: null, error: null as string | null };
    const n = (key: string) => {
      const v = numbers[key];
      return v === "" || v === undefined ? undefined : Number(v);
    };
    const s = (key: string) => selects[key] ?? "";
    const l = (key: string) => parseNumberList(lists[key] ?? "");
    try {
      return { result: spec.compute(n, s, l), error: null as string | null };
    } catch (e) {
      return { result: null, error: e instanceof Error ? e.message : "Ugyldige verdier" };
    }
  }, [spec, numbers, selects, lists]);

  if (!spec) return null;

  const wrongModule = spec.module !== "shared" && activeModuleKey !== spec.module;

  return (
    <>
      <Link
        to="/calculations"
        className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Beregninger
      </Link>
      <PageHeader title={spec.title} subtitle={spec.subtitle} />
      {wrongModule ? (
        <div className="card p-5 text-sm text-muted-foreground">
          Denne beregningen hører til en annen fagmodul enn den som er aktiv.
        </div>
      ) : (
        <CalcShell
          title="Inndata"
          advisory={spec.advisory}
          result={
            outcome.error ? (
              <div className="card p-5 text-sm text-rose-600 dark:text-rose-300">{outcome.error}</div>
            ) : outcome.result ? (
              <ResultCard
                title="Resultat"
                rows={outcome.result.rows}
                ok={outcome.result.ok}
                note={outcome.result.note}
              />
            ) : (
              <div className="card p-5 text-sm text-muted-foreground">Fyll inn verdiene.</div>
            )
          }
        >
          {spec.fields.map((f) =>
            f.type === "select" ? (
              <Field key={f.key} label={f.label}>
                <Select
                  value={selects[f.key] ?? f.default}
                  onChange={(v) => setSelects((prev) => ({ ...prev, [f.key]: v }))}
                  options={f.options}
                />
              </Field>
            ) : f.type === "list" ? (
              <Field key={f.key} label={f.label}>
                <ListInput
                  value={lists[f.key] ?? ""}
                  unit={f.unit}
                  placeholder={f.placeholder}
                  onChange={(v) => setLists((prev) => ({ ...prev, [f.key]: v }))}
                />
              </Field>
            ) : (
              <Field key={f.key} label={f.label}>
                <NumberInput
                  value={numbers[f.key] ?? ""}
                  unit={f.unit}
                  placeholder={f.placeholder}
                  onChange={(v) => setNumbers((prev) => ({ ...prev, [f.key]: v }))}
                />
              </Field>
            ),
          )}
        </CalcShell>
      )}
    </>
  );
}
